import React, { useEffect, useState, createElement } from 'react';
import { ArrowLeftIcon, FileTextIcon, AlertTriangleIcon, CheckCircle2Icon, ArrowRightIcon, MailIcon, CalendarIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
declare global {
  interface Window {
    Tally?: {
      openPopup: (formId: string, options?: Record<string, any>) => void;
      closePopup: (formId: string) => void;
    };
  }
}
export function InvestPage() {
  const [tallyReady, setTallyReady] = useState(false);
  useEffect(() => {
    // Check if Tally is already loaded
    if (window.Tally) {
      setTallyReady(true);
      return;
    }
    const loadTally = () => {
      const existing = document.querySelector('script[src="https://tally.so/widgets/embed.js"]');
      if (existing) {
        // Script tag exists but may not have loaded yet — poll for it
        const interval = setInterval(() => {
          if (window.Tally) {
            setTallyReady(true);
            clearInterval(interval);
          }
        }, 200);
        // Stop polling after 10 seconds
        setTimeout(() => clearInterval(interval), 10000);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://tally.so/widgets/embed.js';
      script.async = true;
      script.onload = () => {
        // Tally may take a moment to initialize after script loads
        const interval = setInterval(() => {
          if (window.Tally) {
            setTallyReady(true);
            clearInterval(interval);
          }
        }, 100);
        setTimeout(() => clearInterval(interval), 5000);
      };
      document.head.appendChild(script);
    };
    loadTally();
  }, []);
  const openInvestorForm = () => {
    if (window.Tally) {
      window.Tally.openPopup('44258r', {
        layout: 'modal',
        width: 700,
        autoClose: 5000
      });
    } else {
      // Fallback: open Tally form in new tab if widget didn't load
      window.open('https://tally.so/r/44258r', '_blank');
    }
  };
  return <div className="min-h-screen bg-gray-50 font-sans text-[#111111]" data-id="element-4244" data-mp="hgse361xeu60">
      {/* Private Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50" data-id="element-4245" data-mp="dg8ja94ts2n1">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-14 md:h-16 flex items-center justify-between" data-id="element-4246" data-mp="8nylk6xgxya7">
          <div className="flex items-center space-x-2 md:space-x-4 min-w-0" data-id="element-4247" data-mp="3p43j77bsdr5">
            <Link to="/investor-portal" className="text-gray-500 hover:text-[#631bb7] transition-colors shrink-0" data-id="element-4248" data-mp="3uwq42qyr003s">
              <ArrowLeftIcon size={18} data-id="element-4249" data-mp="3pz83bnuvwr6v" />
            </Link>
            <div className="h-5 w-px bg-gray-200 shrink-0" data-id="element-4250" data-mp="3lwjql531v04m"></div>
            <img src="https://cdn.magicpatterns.com/uploads/u2RsmpFkExbV8oAVAJrtqh/Caveo-Lockup-FC.png" alt="Caveo" className="h-5 md:h-6 shrink-0" data-id="element-4251" data-mp="3gwozp1j9yajk" />
            <span className="bg-gray-100 text-gray-600 text-[10px] md:text-xs font-semibold px-1.5 md:px-2 py-0.5 md:py-1 rounded-full uppercase tracking-wide shrink-0" data-id="element-4252" data-mp="1f5c0mt3zmwvf">
              Investor Portal
            </span>
          </div>
          <div className="text-xs md:text-sm text-gray-500 shrink-0 hidden sm:block" data-id="element-4253" data-mp="1rmda43nalnzr">
            Confidential & Proprietary
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 md:px-6 py-8 md:py-12 space-y-10" data-id="element-4254" data-mp="21dzi7r4c36ya">
        {/* Hero */}
        <div className="text-center max-w-3xl mx-auto space-y-4" data-id="element-4255" data-mp="13q8v0jywirgz">
          <h1 className="text-[2rem] md:text-[3.25rem] font-semibold text-[#111111] leading-[1.15] tracking-tight" data-id="element-4256" data-mp="13s26qdodg1vu">
            Ready to Invest in Caveo?
          </h1>
        </div>

        {/* Personal Note */}
        <div className="max-w-2xl mx-auto" data-id="element-4257" data-mp="1hpi9nf7gldiu">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 md:p-8" data-id="element-4258" data-mp="1hq9wa0j2w0wz">
            <p className="text-base text-[#111111] leading-relaxed" data-id="element-4259" data-mp="2p2acgbfiy8dl">
              If you made it here, thank you. This round is built on trust, not
              transactions. Take your time with the materials below, and reach
              out anytime if something is unclear or you just want to talk it
              through.
            </p>
            <p className="text-[15px] text-[#111111] mt-4" data-id="element-4260" data-mp="2mqrpt5a3js9z"></p>
            <div className="mt-4 flex items-center gap-3" data-id="element-4261" data-mp="31n8zls2n0yuf">
              <div className="w-px h-8 bg-[#631bb7]" data-id="element-4262" data-mp="2wm1rtt0hvznr"></div>
              <div data-id="element-4263" data-mp="2wonylqfkab4x">
                <p className="font-semibold text-[#111111] text-sm" data-id="element-4264" data-mp="3amcjuzdjjkai">
                  Jason Phillips
                </p>
                <p className="text-xs text-gray-500" data-id="element-4265" data-mp="35nswqvn7o2gk">Founder & CEO</p>
              </div>
            </div>
          </div>
        </div>

        {/* Deal Terms Summary */}
        <div className="max-w-2xl mx-auto" data-id="element-4266" data-mp="3ke04agbok12h">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-4267" data-mp="3fe3ziev5s5lv">
            <div className="px-6 py-4 border-b border-gray-100 bg-[#f8f5fc]" data-id="element-4268" data-mp="3tdvlp2vtwsay">
              <h2 className="text-[1.625rem] md:text-[2.5rem] font-semibold text-[#111111] leading-[1.2]" data-id="element-4269" data-mp="3pbs7i6hrz9lz">
                The Offer at a Glance
              </h2>
            </div>
            <div className="px-6 py-5" data-id="element-4270" data-mp="gt9c26z9j3p2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-4271" data-mp="gs6oo915fjdw">
                <div data-id="element-4272" data-mp="2tqf8abwjes2">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1" data-id="element-4273" data-mp="2sf41pg4n4qx">
                    Round Size
                  </p>
                  <p className="text-lg font-bold text-[#111111]" data-id="element-4274" data-mp="7ue9r5zcadr1">$275,000</p>
                </div>
                <div data-id="element-4275" data-mp="3p786h46l6h0g">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1" data-id="element-4276" data-mp="3tzhxknjftn4b">
                    Instrument
                  </p>
                  <p className="text-lg font-bold text-[#111111]" data-id="element-4277" data-mp="3ga7obfa41f5g">SAFE</p>
                  <p className="text-xs text-gray-500" data-id="element-4278" data-mp="3kazxy9mw9m29">
                    Simple Agreement for Future Equity
                  </p>
                </div>
                <div data-id="element-4279" data-mp="3b9a7p74r0t62">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1" data-id="element-4280" data-mp="3b9tcrn9avn2o">
                    Friends & Family Discount
                  </p>
                  <p className="text-lg font-bold text-[#111111]" data-id="element-4281" data-mp="1guwdfj6fbu5a">25%</p>
                </div>
                <div data-id="element-4282" data-mp="1c08mldny6nnj">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1" data-id="element-4283" data-mp="180i03m5getot">
                    Minimum Investment
                  </p>
                  <p className="text-lg font-bold text-[#111111]" data-id="element-4284" data-mp="133hsv7wvbkw4">$10,000</p>
                </div>
              </div>
              <div className="mt-5 pt-4 border-t border-gray-100 flex items-center gap-2" data-id="element-4285" data-mp="1zpteim22wv8y">
                <div className="w-2 h-2 rounded-full bg-green-500" data-id="element-4286" data-mp="1vo7wkmaotqrw"></div>
                <p className="text-sm font-semibold text-green-700" data-id="element-4287" data-mp="1qpxz251hqe34">
                  Round Status: Open
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Process Steps */}
        <div className="max-w-2xl mx-auto" data-id="element-4288" data-mp="fddmwtw7j91y">
          <div className="space-y-0" data-id="element-4289" data-mp="acg7uq6huv4o">
            <div className="flex gap-4" data-id="element-4290" data-mp="abemnsvrks98">
              <div className="flex flex-col items-center" data-id="element-4291" data-mp="92n6gxde2ml">
                <div className="w-8 h-8 rounded-full bg-[#631bb7] text-white flex items-center justify-center font-bold text-sm shrink-0" data-id="element-4292" data-mp="alydt465nhi">
                  1
                </div>
                <div className="w-0.5 bg-gray-200 flex-1 my-1" data-id="element-4293" data-mp="a6gof9aq2kub"></div>
              </div>
              <div className="pb-6" data-id="element-4294" data-mp="ey76y2jtcewt">
                <p className="text-lg font-semibold text-[#111111]" data-id="element-4295" data-mp="ez9wa61hm9am">
                  Review the disclosures below.
                </p>
              </div>
            </div>
            <div className="flex gap-4" data-id="element-4296" data-mp="nxc0eugipi24">
              <div className="flex flex-col items-center" data-id="element-4297" data-mp="o0gf4lrlzw71">
                <div className="w-8 h-8 rounded-full bg-[#631bb7] text-white flex items-center justify-center font-bold text-sm shrink-0" data-id="element-4298" data-mp="sz1dsgr7q7td">
                  2
                </div>
                <div className="w-0.5 bg-gray-200 flex-1 my-1" data-id="element-4299" data-mp="2upuvpaoob14j"></div>
              </div>
              <div className="pb-6" data-id="element-4300" data-mp="2zno3u99je57g">
                <p className="text-lg font-semibold text-[#111111]" data-id="element-4301" data-mp="2zoppf7o6ia3f">
                  Please review the SAFE agreement.
                </p>
              </div>
            </div>

            <div className="flex gap-4" data-id="element-4302" data-mp="10g7q5605fd47">
              <div className="flex flex-col items-center" data-id="element-4303" data-mp="10f4vcmzpgqj0">
                <div className="w-8 h-8 rounded-full bg-[#631bb7] text-white flex items-center justify-center font-bold text-sm shrink-0" data-id="element-4304" data-mp="1f53w8xinag26">
                  3
                </div>
              </div>
              <div data-id="element-4305" data-mp="mi9bdzhx1eo6">
                <p className="text-lg font-semibold text-[#111111]" data-id="element-4306" data-mp="mhq5jh5jigqi">
                  Once you are ready, click the "Invest Now" button below. We
                  will collect your information and send you an agreement within
                  48 hours.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Important Disclosures */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden max-w-4xl mx-auto" data-id="element-4307" data-mp="26w05zp9es4l8">
          <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 bg-amber-50" data-id="element-4308" data-mp="22v6lz6z379xm">
            <AlertTriangleIcon size={20} className="text-amber-600" data-id="element-4309" data-mp="2adnzdoa338vv" />
            <h2 className="text-[1.625rem] md:text-[2.5rem] font-semibold text-[#111111] leading-[1.2]" data-id="element-4310" data-mp="2ffd990lklgid">
              Important Disclosures
            </h2>
          </div>
          <div className="px-6 py-6 space-y-4 text-gray-600 leading-relaxed text-sm" data-id="element-4311" data-mp="25fw3kj6bj2me">
            <p data-id="element-4312" data-mp="2t5qg0zmq6p64"></p>
            <p data-id="element-4313" data-mp="2t56yhk0zm2sm">
              Investments in AppEmbrace, Inc. (d/b/a Caveo) are available only
              to accredited investors as defined under Rule 501 of Regulation D
              of the Securities Act of 1933, as amended. An accredited investor
              generally includes individuals with an annual income exceeding
              $200,000 (or $300,000 jointly with a spouse) in each of the two
              most recent years, or a net worth exceeding $1,000,000, excluding
              the value of their primary residence.
            </p>
            <p data-id="element-4314" data-mp="3gxe0ozte93zy">
              Investing in early-stage companies involves a high degree of risk.
              You may lose your entire investment. These securities are
              illiquid, meaning there is no guaranteed market for resale. Past
              performance of any company or investment is not indicative of
              future results.
            </p>
            <p data-id="element-4315" data-mp="atl166grbsyk"></p>
            <p className="font-medium text-gray-700" data-id="element-4316" data-mp="at1jmqv0r6l2">
              This offering has not been reviewed or approved by the U.S.
              Securities and Exchange Commission or any state securities
              regulator.
            </p>
          </div>
        </div>

        {/* Embedded SAFE Agreement PDF */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden max-w-4xl mx-auto" data-id="element-4317" data-mp="3j8lbblg1kgy0">
          <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 bg-gray-50" data-id="element-4318" data-mp="39ci626pw826u">
            <FileTextIcon size={20} className="text-[#631bb7]" data-id="element-4319" data-mp="3eblcnvliuggx" />
            <h2 className="text-[1.625rem] md:text-[2.5rem] font-semibold text-[#111111] leading-[1.2]" data-id="element-4320" data-mp="3eathglvu6kr6">
              SAFE Agreement
            </h2>
          </div>
          <div className="w-full" style={{
          height: '80vh'
        }} data-id="element-4321" data-mp="2qozo3r0yx2ey">
            <iframe src="https://drive.google.com/file/d/1w59cWXbjVzlR-My86Eke0EnrsG9-xluU/preview" className="w-full h-full border-0" allow="autoplay" title="Caveo SAFE Agreement" data-id="element-4322" data-mp="34pk64kz26u7e" />
          </div>
        </div>

        {/* Contact, Calendar & Invest */}
        <div className="max-w-md mx-auto" data-id="element-4323" data-mp="39kzgv41rs9zl">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 md:p-8 text-center" data-id="element-4324" data-mp="3neqf3ga1w7g0">
            <h2 className="text-[1.625rem] md:text-[2.5rem] font-semibold text-[#111111] leading-[1.2] mb-2" data-id="element-4325" data-mp="3if45dgtfl9by">
              Have Questions Before You Commit?
            </h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-6" data-id="element-4326" data-mp="3sdtnnap8ovsq">
              Email me or book a meeting.
            </p>
            <div className="flex flex-col gap-3" data-id="element-4327" data-mp="3cf194blm2cj">
              <a href="mailto:jbp@caveohq.com" className="w-full inline-flex items-center justify-center gap-2 bg-white text-[#631bb7] border border-[#631bb7] px-6 py-3 rounded-lg font-medium text-sm hover:bg-[#f8f5fc] transition-colors no-underline" data-id="element-4328" data-mp="39j4hjbhljpo">
                <MailIcon size={16} data-id="element-4329" data-mp="i231e281muyo" />
                Email Me
              </a>
              <a href="https://calendar.app.google/EnoqEH7z4cY89jjN8" target="_blank" rel="noopener noreferrer" className="w-full inline-flex items-center justify-center gap-2 bg-white text-[#631bb7] border border-[#631bb7] px-6 py-3 rounded-lg font-medium text-sm hover:bg-[#f8f5fc] transition-colors no-underline" data-id="element-4330" data-mp="mz37gd4w7xps">
                <CalendarIcon size={16} data-id="element-4331" data-mp="2xt8ticmbx0xy" />
                Book a Meeting
              </a>
              <div className="pt-3 mt-1 border-t border-gray-100" data-id="element-4332" data-mp="32r0urx8v5zet">
                <button onClick={openInvestorForm} className="w-full inline-flex items-center justify-center bg-[#631bb7] text-white px-8 py-3.5 rounded-lg font-medium hover:bg-[#521099] transition-colors text-base shadow-sm cursor-pointer border-0" data-id="element-4333" data-mp="2v7832m6spab7">
                  Invest Now
                  <ArrowRightIcon size={20} className="ml-2" data-id="element-4334" data-mp="2gjwvevdqiagd" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <footer className="bg-white border-t border-gray-200 py-8 mt-12" data-id="element-4335" data-mp="396hllsih4gic">
        <div className="max-w-7xl mx-auto px-6 text-center text-sm text-gray-500" data-id="element-4336" data-mp="347ef03mui289">
          <p data-id="element-4337" data-mp="349hfjhlmjdr8">&copy; 2026 AppEmbrace, Inc. DBA Caveo. All rights reserved.</p>
          <p className="mt-2 text-xs text-gray-400" data-id="element-4338" data-mp="2z8bm1hk53mgn">
            This page contains confidential information intended only for
            authorized investors. Do not share or distribute without permission.
          </p>
        </div>
      </footer>
    </div>;
}